// src/Dashboard/admin/components/LearnerDrawer.jsx
import { X, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { Badge } from "./ui";

export default function LearnerDrawer({
  open,
  onClose,
  learner,
  churchLabel = "—",
  loading = false,
  error = "",
  rows = [],
  courseSlug = "",
  onCourseSlugChange,
  onApplyCourseSlug, // called onBlur + refresh
}) {
  if (!open) return null;

  const safeRows = Array.isArray(rows) ? rows : [];

  const name =
    learner?.fullName ||
    learner?.full_name ||
    learner?.email ||
    "Learner";

  const email = learner?.email || "—";

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm text-slate-500">Learner details</div>
            <div className="text-lg font-semibold truncate">{name}</div>
            <div className="text-xs text-slate-500 truncate">
              {email} • {churchLabel}
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-auto">
          {error ? (
            <div className="p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-100">
              {String(error)}
            </div>
          ) : null}

          <div className="bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold">Course rollup</div>

              <button
                onClick={() => onApplyCourseSlug?.()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-xs hover:bg-white dark:hover:bg-slate-800"
                type="button"
              >
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>

            <div className="mt-3">
              <label className="block text-xs font-semibold text-slate-600 mb-2">
                Filter by course (optional)
              </label>

              <input
                value={courseSlug}
                onChange={(e) => onCourseSlugChange?.(e.target.value)}
                onBlur={() => onApplyCourseSlug?.()}
                placeholder="discover"
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
              />

              <div className="mt-2 text-xs text-slate-500">
                Leave blank for all courses, or type a course slug and click outside (or Refresh).
              </div>
            </div>
          </div>

          <div className="overflow-auto rounded-xl border border-slate-200 dark:border-slate-800">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/70 text-slate-700 dark:text-slate-200">
                <tr>
                  <th className="p-3 text-left">Course</th>
                  <th className="p-3 text-center">Lessons</th>
                  <th className="p-3 text-center">Attempts</th>
                  <th className="p-3 text-center">Best %</th>
                  <th className="p-3 text-center">Certificate</th>
                </tr>
              </thead>

              <tbody>
                {safeRows.map((r, idx) => {
                  const slug = r.course_slug || r.courseSlug || "—";
                  const lessons = r.lessons_completed ?? r.lessonsCompleted ?? 0;
                  const attempts = r.attempts ?? r.total_attempts ?? 0;
                  const best = Math.round(r.best_score_pct ?? r.bestScorePct ?? 0);
                  const hasCert = !!(r.has_certificate || r.hasCertificate || r.certificate_url);

                  return (
                    <tr
                      key={r.course_slug || r.courseSlug || idx}
                      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <td className="p-3 font-semibold">{slug}</td>
                      <td className="p-3 text-center">{lessons}</td>
                      <td className="p-3 text-center">{attempts}</td>
                      <td className="p-3 text-center">{best}%</td>
                      <td className="p-3 text-center">
                        {hasCert ? (
                          <Badge tone="success" icon={CheckCircle2} label="Yes" />
                        ) : (
                          <Badge tone="neutral" icon={XCircle} label="No" />
                        )}
                      </td>
                    </tr>
                  );
                })}

                {safeRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-10 text-center text-slate-500">
                      {loading ? "Loading…" : "No rollup data found."}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <div className="text-xs text-slate-500">
            This drawer uses your one-user rollup route (fast even for big data).
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-slate-200 dark:border-slate-800">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 rounded-xl bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900 font-semibold"
            type="button"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
